/**
 * Modern WYSIWYG editor for minisite text blocks.
 *
 * Built on TipTap (ProseMirror) so we get a proper editor model instead of
 * relying on the deprecated `document.execCommand` (which required prior
 * selection and behaved poorly on mobile).
 *
 * Key UX improvements over the legacy editor:
 *  - Toggle bold/italic/underline by clicking the button (no need to
 *    pre-select text — works while typing too).
 *  - Smart link insertion: detects email / phone / URL and auto-prefixes
 *    `mailto:` / `tel:` / `https://`.
 *  - Quick-insert buttons for the club's email and phone.
 *  - Touch-friendly buttons with active state indicators.
 *  - Live preview toggle to see the final rendering.
 *  - Backwards compatible: reads/writes raw HTML, no migration needed.
 */
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import LinkExtension from "@tiptap/extension-link";
import { useState, useCallback, useEffect } from "react";
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Link as LinkIcon,
  Image as ImageIcon,
  List,
  Heading2,
  Heading3,
  Mail,
  Phone,
  Eye,
  Pencil,
  Unlink,
} from "lucide-react";

const CLUB_EMAIL = "secretaria@aravellgolfclub.com";
const CLUB_PHONE = "+34661194023";

const LINK_CLASS = "text-[#1A4D2E] underline hover:text-[#D4AF37]";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^[+\d][\d\s\-().]{4,}$/;

/** Turn a user-typed string into a properly prefixed URL. */
const normalizeUrl = (raw) => {
  if (!raw) return null;
  const s = raw.trim();
  if (!s) return null;
  if (s.startsWith("mailto:") || s.startsWith("tel:")) return s;
  if (EMAIL_RE.test(s)) return `mailto:${s}`;
  if (PHONE_RE.test(s) && !s.includes(" ")) return `tel:${s.replace(/[\s().-]/g, "")}`;
  if (/^https?:\/\//i.test(s)) return s;
  if (s.startsWith("/") || s.startsWith("#")) return s;
  return `https://${s}`;
};

const ToolbarButton = ({ active, onClick, title, children, danger }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    aria-pressed={active}
    className={`px-2.5 py-1.5 rounded text-sm flex items-center gap-1 transition-colors select-none
      ${active
        ? "bg-[#1A4D2E] text-white"
        : danger
        ? "text-red-600 hover:bg-red-50"
        : "text-neutral-700 hover:bg-white"}`}
  >
    {children}
  </button>
);

const ToolbarSep = () => <div className="w-px bg-neutral-300 mx-0.5 h-6 self-center" />;

export const MinisiteRichEditor = ({ value, onChange }) => {
  const [preview, setPreview] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // We provide Link separately to enable autolinking and custom class
        link: false,
      }),
      LinkExtension.configure({
        openOnClick: false,
        autolink: true,
        defaultProtocol: "https",
        HTMLAttributes: {
          class: LINK_CLASS,
          target: "_blank",
          rel: "noopener noreferrer",
        },
      }),
    ],
    content: value || "",
    onUpdate: ({ editor: e }) => {
      const html = e.getHTML();
      // TipTap returns "<p></p>" for empty — keep an empty string instead so
      // the DB stays clean.
      onChange(html === "<p></p>" ? "" : html);
    },
  });

  // Keep the editor in sync when `value` prop changes externally
  // (e.g. switching between blocks).
  useEffect(() => {
    if (!editor) return;
    if ((value || "") !== editor.getHTML()) {
      editor.commands.setContent(value || "", { emitUpdate: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  const insertLink = useCallback(() => {
    if (!editor) return;
    const previous = editor.getAttributes("link").href || "";
    const raw = window.prompt(
      "URL, email o telèfon (afegirem mailto:/tel:/https:// automàticament):",
      previous
    );
    if (raw === null) return; // cancel
    if (raw.trim() === "") {
      editor.chain().focus().unsetLink().run();
      return;
    }
    const url = normalizeUrl(raw);
    if (!url) return;

    // If nothing is selected, insert the URL/label as the link text.
    const { from, to, empty } = editor.state.selection;
    if (empty) {
      const label = raw.trim();
      editor
        .chain()
        .focus()
        .insertContent(
          `<a href="${url}" target="_blank" rel="noopener noreferrer" class="${LINK_CLASS}">${label}</a>`
        )
        .run();
    } else {
      editor.chain().focus().setLink({ href: url }).run();
    }
    void from;
    void to;
  }, [editor]);

  const removeLink = useCallback(() => {
    editor?.chain().focus().unsetLink().run();
  }, [editor]);

  const insertImage = useCallback(() => {
    if (!editor) return;
    const url = window.prompt("URL de la imatge:");
    if (!url) return;
    const alt = window.prompt("Text alternatiu (opcional):") || "";
    const width = window.prompt("Amplada en píxels (buit = automàtic):") || "";
    const widthStyle = width
      ? `max-width: ${width}px; height: auto;`
      : "max-width: 100%; height: auto;";
    editor
      .chain()
      .focus()
      .insertContent(
        `<img src="${url}" alt="${alt}" style="${widthStyle}" class="rounded-lg my-2" />`
      )
      .run();
  }, [editor]);

  const quickEmail = useCallback(() => {
    if (!editor) return;
    editor
      .chain()
      .focus()
      .insertContent(
        `<a href="mailto:${CLUB_EMAIL}" class="${LINK_CLASS}">${CLUB_EMAIL}</a>`
      )
      .run();
  }, [editor]);

  const quickPhone = useCallback(() => {
    if (!editor) return;
    const display = "661 19 40 23";
    editor
      .chain()
      .focus()
      .insertContent(
        `<a href="tel:${CLUB_PHONE}" class="${LINK_CLASS}">${display}</a>`
      )
      .run();
  }, [editor]);

  if (!editor) return null;

  const linkActive = editor.isActive("link");

  return (
    <div className="border border-[#E5E5E5] rounded-lg overflow-hidden bg-white">
      {/* Toolbar */}
      <div
        className="bg-[#F9F9F7] border-b border-[#E5E5E5] p-1.5 flex flex-wrap gap-0.5 items-center"
        data-testid="minisite-editor-toolbar"
      >
        <ToolbarButton
          active={editor.isActive("bold")}
          onClick={() => editor.chain().focus().toggleBold().run()}
          title="Negreta (Ctrl+B)"
        >
          <Bold className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive("italic")}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          title="Cursiva (Ctrl+I)"
        >
          <Italic className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive("underline")}
          onClick={() => editor.chain().focus().toggleUnderline?.().run()}
          title="Subratllat (Ctrl+U)"
        >
          <UnderlineIcon className="w-4 h-4" />
        </ToolbarButton>

        <ToolbarSep />

        <ToolbarButton
          active={linkActive}
          onClick={insertLink}
          title="Inserir / editar enllaç"
        >
          <LinkIcon className="w-4 h-4" />
        </ToolbarButton>
        {linkActive && (
          <ToolbarButton onClick={removeLink} title="Treure enllaç" danger>
            <Unlink className="w-4 h-4" />
          </ToolbarButton>
        )}
        <ToolbarButton onClick={insertImage} title="Inserir imatge">
          <ImageIcon className="w-4 h-4" />
        </ToolbarButton>

        <ToolbarSep />

        <ToolbarButton
          active={editor.isActive("bulletList")}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          title="Llista"
        >
          <List className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive("heading", { level: 2 })}
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 2 }).run()
          }
          title="Títol H2"
        >
          <Heading2 className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive("heading", { level: 3 })}
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 3 }).run()
          }
          title="Subtítol H3"
        >
          <Heading3 className="w-4 h-4" />
        </ToolbarButton>

        <ToolbarSep />

        <ToolbarButton onClick={quickEmail} title={`Inserir email del club (${CLUB_EMAIL})`}>
          <Mail className="w-4 h-4" />
          <span className="hidden sm:inline text-xs">Email</span>
        </ToolbarButton>
        <ToolbarButton onClick={quickPhone} title={`Inserir telèfon del club (${CLUB_PHONE})`}>
          <Phone className="w-4 h-4" />
          <span className="hidden sm:inline text-xs">Tel</span>
        </ToolbarButton>

        <div className="ml-auto">
          <ToolbarButton
            active={preview}
            onClick={() => setPreview((p) => !p)}
            title={preview ? "Tornar a edició" : "Vista prèvia"}
          >
            {preview ? (
              <>
                <Pencil className="w-4 h-4" />
                <span className="hidden sm:inline text-xs">Editar</span>
              </>
            ) : (
              <>
                <Eye className="w-4 h-4" />
                <span className="hidden sm:inline text-xs">Previsualitzar</span>
              </>
            )}
          </ToolbarButton>
        </div>
      </div>

      {/* Editor or preview */}
      {preview ? (
        <div
          className="min-h-[120px] p-4 prose prose-sm max-w-none"
          dangerouslySetInnerHTML={{ __html: value || "" }}
          data-testid="minisite-editor-preview"
        />
      ) : (
        <EditorContent
          editor={editor}
          className="minisite-editor-content"
        />
      )}
    </div>
  );
};

export default MinisiteRichEditor;
